import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { MSpinner, MTable } from "components/Shared";
import Button from "@mui/material/Button";
import MDialog from "components/Shared/MDialog.tsx";
import {
  getNextNotificationCoalesced,
  invalidateNextNotification,
  refreshNextNotification,
} from "requests/schedulingAssistant";
import { updateCallDueDate } from "requests/contactEfforts";
import EditNotificationDueDateDialog from "./EditNotificationDueDateDialog.tsx";

const NotificationStep = props => {
  const [fetchingNotificationLog, setFetchingNotificationLog] = useState(false);  
  const [notificationLog, setNotificationLog] = useState([]);
  const [notificationOverdue, setNotificationOverdue] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const mounted = useRef(true);
  const notificationRequestCleanup = useRef(() => {});

  const applyNotificationResponse = (resp) => {
    if (mounted.current) {
      setNotificationLog([resp]);
      setNotificationOverdue(Date.parse(resp.nextNotificationDate) <= Date.now());
      setFetchingNotificationLog(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    setShowEditDialog(false);
    setNotificationLog([]);
    setNotificationOverdue(false);
    setFetchingNotificationLog(true);
    fetchNotifications();
    const interval = setInterval(() => {
      fetchNotifications(true);
    }, 10000); // Refreshing call log every 10 seconds to pick up on events from PNX

    return () => {
      mounted.current = false;
      notificationRequestCleanup.current();
      clearInterval(interval);
    };
  }, [props.controlNumber]);

  let fetchNotifications = (forceRefresh = false) => {
    notificationRequestCleanup.current();
    const requestMethod = forceRefresh ? refreshNextNotification : getNextNotificationCoalesced;
    notificationRequestCleanup.current = requestMethod(props.controlNumber, applyNotificationResponse, () => {
      if (mounted.current) {
        setFetchingNotificationLog(false);
      }
    });
  };

  let onEditDueDateSave = (data) => {
    updateCallDueDate({
      ControlIdNumber: props.controlNumber,
      CurrentDueDate: notificationLog[0]?.nextNotificationDate,
      NewDueDate: data.newDueDate
    }, () => {
      invalidateNextNotification(props.controlNumber);
      setShowEditDialog(false);
      setFetchingNotificationLog(true);
      fetchNotifications(true);
    }, () => setShowEditDialog(false), mounted);
  };

  const colDefs = [
    { name: "Next Notification", dataSource: "nextNotificationName", style: { width: "200px" } },
    { name: "Next Notification Due Date", dataSource: "nextNotificationDateFormatted", style: 
      { width: "350px", color: notificationOverdue ? "red" : ""} 
    },
    { name: "Previous Notification", dataSource: "previousNotificationName", style: { width: "200px" } },
    { name: "Previous Notification Date", dataSource: "previousNotificationDateFormatted", style: { width: "350px" } },
  ];
  const tableData = notificationLog.map((entry, index) => ({
    ...entry,
    rowId: `next-notification-${index}`,
  }));

  if(fetchingNotificationLog){
    return <MSpinner />;
  }

  return (
    <>
      <MDialog
        id="add-edit-notification-due-date-dialog"
        className="add-edit-notification-due-date-dialog mgridform-flat"
        title="Edit Call Due Date"
        visible={showEditDialog}
        focusOnMount={false}
        width={550}
      >
        {fetchingNotificationLog 
          ? <MSpinner />
          : <EditNotificationDueDateDialog
              data={{
                currentDueDate: notificationLog[0]?.nextNotificationDateFormatted,
                newDueDate: ""
              }}
              onCancel={() => setShowEditDialog(false)}
              onSave={onEditDueDateSave}
            />}
      </MDialog>   
      <div className="notification-due-date-buttons">
        <div className="notification-due-date-button">
          <Button className="history-action-button" onClick={() => setShowEditDialog(true)} disabled={!notificationLog[0]?.nextNotificationDateFormatted}>
            Edit
        </Button></div>
      </div>
      <MTable
        className="tabbed-history"
        keyProperty="rowId"
        columnDefinitions={colDefs}
        data={tableData}
        emptyTableText="No Next Notification Entered"
      />
    </>
  );
};

NotificationStep.propTypes = {
  controlNumber: PropTypes.number,
};

export default NotificationStep;